/* MIKOO wall preview: the picture (or the whole set) on the shopper's own wall, at real size.
   1. a photo of the wall (or the sample wall), 2. one known length to set the scale,
   3. the frames, drawn like the live frame preview (mikoo-frame.js), dragged into place.
   Everything is drawn on one canvas in photo pixels, so "save" exports exactly what is on screen.
   The photo never leaves the device. */
(function () {
  var GAP_CM = 10; /* space between pictures in a set, same as the wall drawing in mikoo-product-extras.js */
  var MAX_PHOTO = 2000; /* longest side of the working photo, keeps phones fast */
  var HANDLE_PX = 13; /* measuring handle radius on screen */
  var SAMPLE = { wallCm: 360, heightCm: 250, pxPerCm: 4, sofaCm: 200, sofaHeightCm: 85, floorCm: 12 };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var dialog, canvas, ctx, hint;
  var product = null;
  var pictures = []; /* loaded unframed images */
  var listed = null; /* sets with fixed mixed sizes: [{ name, w, h, src, img }] from the product details */
  var listedFrame = null;
  var canExport = true;
  var photo = null; /* canvas holding the wall photo */
  var pxPerCm = 0;
  var isSample = false;
  var mode = 'start'; /* start | measure | place */
  var view = { k: 1, dpr: 1 }; /* screen px per photo px */
  var line = { a: { x: 0, y: 0 }, b: { x: 0, y: 0 } };
  var art = { x: 0, y: 0 };
  var springX, springY;
  var drag = null;
  var variant = null;
  var sizeIndex = -1;
  var frameIndex = -1;
  var drawQueued = false;

  /* same frames as the live frame preview in mikoo-frame.js: real profiles in cm, colours sampled from the frame photos */
  var WOOD = { profile: 1.5, depth: 3 };
  var METAL = { profile: 0.7, depth: 2.5 };
  var FRAMES = {
    'עץ שחור': { dims: WOOD, base: '#121214', light: '#2a2a30', dark: '#060607' },
    'עץ טבעי': { dims: WOOD, base: '#e6c88f', light: '#f3dcae', dark: '#d4b48b', tex: 'natural' },
    'עץ מולבן': { dims: WOOD, base: '#f1e8d9', light: '#f8efe0', dark: '#e6dccb', tex: 'whitewashed' },
    'עץ אלון': { dims: WOOD, base: '#bfa074', light: '#dcc092', dark: '#a98a5c', tex: 'oak' },
    'אלומיניום שחור': { dims: METAL, base: '#0c0c0e', light: '#303034', dark: '#020203' },
    'אלומיניום לבן': { dims: METAL, base: '#efefef', light: '#fafafa', dark: '#e0e0e0' }
  };
  function frames() { return FRAMES; }
  function noFrame() { return 'ללא מסגרת'; }

  function track(name) {
    if (window.mkTrack) window.mkTrack('mk_wall_preview_' + name);
  }

  /* The unframed pictures, from the product's image file names. Usually they end in 1 (framed: 2, room: 3).
     Before comparing, Shopify's _<uuid> and _<n> suffixes for repeated names are removed (a uuid can end in 1).
     When the product has a framed picture ("…2"), only the "…1" with the same name counts (so a room picture
     like "…no131" next to "…no11" / "…no12" is left out). Old uploads with no number: the unframed picture is
     named like the framed one without the 2. Same rule as snippets/mikoo-frame-stage.liquid.
     isPlain(src, all): all = every image of the product (srcs or objects with .src). */
  function stemOf(src) {
    var m = /([^/]+)[.](jpe?g|png|webp)$/i.exec(String(src || '').split('?')[0]);
    return m ? m[1].replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '').replace(/_[0-9]+$/, '') : null;
  }
  function plainStems(all) {
    var stems = (all || []).map(function (x) { return stemOf(typeof x === 'string' ? x : x && (x.src || x.url)); }).filter(Boolean);
    var ones = stems.filter(function (s) { return /1$/.test(s); });
    var bases = stems.filter(function (s) { return /2$/.test(s); }).map(function (s) { return s.slice(0, -1); });
    var matched = ones.filter(function (s) { return bases.indexOf(s.slice(0, -1)) > -1; });
    if (matched.length) return matched;
    if (ones.length) return ones;
    return stems.filter(function (s) { return !/[0-9]$/.test(s) && stems.indexOf(s + '2') > -1; });
  }
  function isPlain(src, all) {
    var stem = stemOf(src);
    return !!stem && plainStems(all && all.length ? all : [src]).indexOf(stem) > -1;
  }

  function sized(src, width) {
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function formatMoney(cents) {
    var value = cents / 100;
    return '₪' + (value % 1 === 0 ? value.toLocaleString('he-IL') : value.toLocaleString('he-IL', { minimumFractionDigits: 2 }));
  }

  /* ---------- product, variants and the current choice ---------- */
  /* one request, shared; product is only set once its pictures have loaded, so nothing draws half-ready */
  var productLoading = null;
  function loadProduct() {
    if (!productLoading) productLoading = fetchProduct().then(function (p) {
      product = p;
      pickInitialVariant();
      return p;
    }, function (error) {
      productLoading = null; /* allow a retry */
      throw error;
    });
    return productLoading;
  }

  function fetchProduct() {
    return fetch('/products/' + dialog.dataset.handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) {
        p.options.forEach(function (option, i) {
          var name = (option.name || option).toString();
          if (name === 'גודל') sizeIndex = i;
          if (name === 'מסגרת') frameIndex = i;
        });
        var images = (p.media || []).filter(function (m) { return m.media_type === 'image'; });
        /* sets without a size option list each picture and its fixed size in the product details */
        listed = sizeIndex === -1 ? readList(images) : null;
        if (listed) {
          return Promise.all(listed.map(function (item) { return item.src ? loadImage(sized(item.src, 1200)) : null; })).then(function (loaded) {
            listed.forEach(function (item, i) { item.img = loaded[i]; });
            pictures = loaded.filter(Boolean);
            return p;
          });
        }
        var plain = images.filter(function (m) { return isPlain(m.src, images); });
        if (!plain.length && p.featured_image) plain = [{ src: p.featured_image }];
        return Promise.all(plain.map(function (m) { return loadImage(sized(m.src, 1200)); })).then(function (loaded) {
          pictures = loaded.filter(Boolean);
          return p;
        });
      });
  }

  /* "<p>WHITE ROSE STONES - 21x30</p> ... <p>צבע מסגרת - עץ שחור</p>" -> pictures with sizes, matched to
     their image by name (the unframed file is the name without spaces, e.g. WHITEROSESTONES1_….jpg) */
  function readList(images) {
    var el = document.querySelector('[data-mk-wp-list]');
    var html = '';
    try { html = el ? JSON.parse(el.textContent) || '' : ''; } catch (e) { return null; }
    var text = html.replace(/<\/p>|<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
      .replace(/\n\s*(?=-\s*[0-9]+\s*[xX×])/g, ' '); /* a size that slipped onto its own line belongs to the name above */
    var items = [];
    listedFrame = null;
    text.split('\n').forEach(function (raw) {
      var row = raw.trim();
      var size = /^(.*?)\s*-\s*([0-9]+)\s*[xX×]\s*([0-9]+)\s*$/.exec(row);
      if (size) { items.push({ name: size[1], w: parseInt(size[2], 10), h: parseInt(size[3], 10), src: null, img: null }); return; }
      if (row.indexOf('צבע מסגרת') === 0) {
        listedFrame = Object.keys(FRAMES).filter(function (name) { return row.indexOf(name) > -1; })[0] || null;
      }
    });
    if (!items.length) return null;

    function key(s) { return s.toLowerCase().replace(/[^a-z0-9]/g, ''); }
    function fileKey(src) {
      var file = src.split('?')[0].split('/').pop().replace(/[.][a-z]+$/i, '').replace(/_[0-9a-f]{8}-[0-9a-f-]+$/i, '');
      return key(file.replace(/1$/, ''));
    }
    function pairs(s) { var out = []; for (var i = 0; i < s.length - 1; i++) out.push(s.substr(i, 2)); return out; }
    function similar(a, b) {
      if (!a || !b) return 0;
      if (a === b) return 2;
      if (a.indexOf(b) > -1 || b.indexOf(a) > -1) return 1.5;
      var A = pairs(a), B = pairs(b).slice(), hits = 0;
      A.forEach(function (g) { var j = B.indexOf(g); if (j > -1) { hits++; B.splice(j, 1); } });
      return (2 * hits) / (A.length + B.length + hits || 1);
    }
    /* best pairs first, each name and each image used once */
    var scores = [];
    items.forEach(function (item, i) {
      images.forEach(function (m, j) {
        var score = similar(key(item.name), fileKey(m.src)) + (isPlain(m.src, images) ? 0.1 : 0);
        if (score >= 0.55) scores.push({ i: i, j: j, score: score });
      });
    });
    scores.sort(function (a, b) { return b.score - a.score; });
    var usedImage = {};
    scores.forEach(function (s) {
      if (items[s.i].src || usedImage[s.j]) return;
      items[s.i].src = images[s.j].src;
      usedImage[s.j] = true;
    });
    /* a few file names differ from the written name (BIG EYE / BlackEye1): give the rest the unused pictures */
    var spare = images.filter(function (m, j) { return !usedImage[j] && isPlain(m.src, images); });
    items.forEach(function (item) { if (!item.src && spare.length) item.src = spare.shift().src; });
    return items;
  }

  /* loads with CORS so the canvas can still be saved; falls back to a plain load (then no saving) */
  function loadImage(src) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () {
        var plain = new Image();
        plain.onload = function () { canExport = false; resolve(plain); };
        plain.onerror = function () { resolve(null); };
        plain.src = src;
      };
      img.src = src;
    });
  }

  function pageValues() {
    var root = document.querySelector('variant-selects');
    var values = [];
    if (!root) return values;
    root.querySelectorAll('input[type="radio"]:checked').forEach(function (input) { values.push(input.value); });
    root.querySelectorAll('select').forEach(function (select) { values.push(select.value); });
    return values;
  }

  /* start from what is selected on the page */
  function pickInitialVariant() {
    var values = pageValues();
    var match = product.variants.filter(function (v) {
      return v.options.every(function (value) { return values.indexOf(value) > -1; });
    })[0];
    variant = match || product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
  }

  function choose(optionIndex, value) {
    var wanted = variant.options.slice();
    wanted[optionIndex] = value;
    var exact = product.variants.filter(function (v) {
      return v.options.every(function (o, i) { return o === wanted[i]; });
    })[0];
    var near = product.variants.filter(function (v) { return v.options[optionIndex] === value; });
    /* a bigger size grows upwards, like a picture hung above furniture */
    var bottom = art.y + (layout().h * pxPerCm) / 2;
    variant = exact || near.filter(function (v) { return v.available; })[0] || near[0] || variant;
    if (springX) { springX.stop(); springY.stop(); }
    art.y = bottom - (layout().h * pxPerCm) / 2;
    renderOptions();
    clampArt(false);
  }

  function sizeCm() {
    var value = sizeIndex > -1 ? variant.options[sizeIndex] : '';
    var match = /([0-9]+) *[xX×] *([0-9]+)/.exec(value || '');
    if (!match) return null;
    return { w: parseInt(match[1], 10), h: parseInt(match[2], 10) };
  }

  /* sets sold in size pairs such as "70x70 + 50x70": each picture takes the size closest to its own shape
     (as comboSizes in mikoo-frame.js). Null for an ordinary size. */
  function comboCm(imgs) {
    var value = sizeIndex > -1 ? variant.options[sizeIndex] : '';
    var parts = String(value || '').split('+');
    if (parts.length < 2) return null;
    var sizes = parts.map(function (part) { var m = /^\s*([0-9]+)\s*[xX×]\s*([0-9]+)\s*$/.exec(part); return m ? { w: +m[1], h: +m[2] } : null; });
    if (sizes.some(function (s) { return !s; })) return null;
    return imgs.map(function (img) {
      var r = img && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0.75, best = -1;
      sizes.forEach(function (s, k) {
        if (s.used) return;
        if (best < 0 || Math.abs(Math.log(r / (s.w / s.h))) < Math.abs(Math.log(r / (sizes[best].w / sizes[best].h)))) best = k;
      });
      if (best < 0) return { w: sizes[0].w, h: sizes[0].h };
      sizes[best].used = true;
      return { w: sizes[best].w, h: sizes[best].h };
    });
  }

  /* sizes are written width first for portrait pictures; a landscape picture hangs the other way */
  function oriented(w, h, img) {
    if (img && img.naturalWidth > img.naturalHeight * 1.05 && w < h) return { w: h, h: w };
    return { w: w, h: h };
  }

  function frameDef() {
    var value = frameIndex > -1 ? variant.options[frameIndex] : (listedFrame || '');
    if (value.indexOf(noFrame()) > -1) return null;
    return frames()[value] || (frameIndex > -1 ? null : frames()['עץ שחור']);
  }

  /* ---------- the group of frames, in centimetres ---------- */
  function layout() {
    var def = frameDef();
    var p = def ? def.dims.profile : 0;
    var items;
    if (listed) {
      items = listed.map(function (item) {
        var size = oriented(item.w, item.h, item.img);
        return { w: size.w + 2 * p, h: size.h + 2 * p, img: item.img };
      });
    } else {
      var size = sizeCm() || { w: 30, h: 40 };
      var imgs = pictures.length ? pictures : [null];
      var each = comboCm(imgs);
      items = imgs.map(function (img, i) {
        var s = each ? each[i] : oriented(size.w, size.h, img);
        return { w: s.w + 2 * p, h: s.h + 2 * p, img: img };
      });
    }
    var mixed = items.some(function (it) { return it.w !== items[0].w || it.h !== items[0].h; });
    var placed = mixed ? galleryLayout(items) : gridLayout(items);
    return { def: def, profile: p, items: placed.items, w: placed.w, h: placed.h };
  }

  /* same-size pictures: up to 3 in a row, more in two rows; RTL, the first picture hangs on the right */
  function gridLayout(items) {
    var n = items.length;
    var cols = n <= 3 ? n : Math.ceil(n / 2);
    var rows = Math.ceil(n / cols);
    var fw = items[0].w, fh = items[0].h;
    var w = cols * fw + (cols - 1) * GAP_CM;
    items.forEach(function (it, i) {
      var col = i % cols;
      var row = Math.floor(i / cols);
      var inRow = row === rows - 1 ? n - row * cols : cols; /* the last row of an uneven set is centred */
      var offset = ((cols - inRow) * (fw + GAP_CM)) / 2;
      it.x = offset + (inRow - 1 - col) * (fw + GAP_CM);
      it.y = row * (fh + GAP_CM);
    });
    return { items: items, w: w, h: rows * fh + (rows - 1) * GAP_CM };
  }

  /* mixed sizes, hung like a gallery wall: the biggest picture in the middle on one centre line,
     the others in columns of up to two on either side, bigger ones closer to the middle */
  function pairUp(list) {
    var cols = [];
    for (var i = 0; i < list.length; i += 2) cols.push(list.slice(i, i + 2));
    return cols;
  }
  function galleryLayout(items) {
    var order = items.slice().sort(function (a, b) { return b.w * b.h - a.w * a.h; });
    var centre = order[0];
    centre.x = -centre.w / 2;
    centre.y = -centre.h / 2;
    var sides = [[], []];
    order.slice(1).forEach(function (it, i) { sides[i % 2].push(it); });
    sides.forEach(function (side, s) {
      var dir = s === 0 ? 1 : -1;
      var cursor = centre.w / 2 + GAP_CM;
      pairUp(side).forEach(function (col) {
        var colW = Math.max.apply(null, col.map(function (it) { return it.w; }));
        var colH = col.reduce(function (sum, it) { return sum + it.h; }, 0) + (col.length - 1) * GAP_CM;
        var y = -colH / 2;
        col.forEach(function (it) {
          var mid = dir * (cursor + colW / 2);
          it.x = mid - it.w / 2;
          it.y = y;
          y += it.h + GAP_CM;
        });
        cursor += colW + GAP_CM;
      });
    });
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    items.forEach(function (it) {
      minX = Math.min(minX, it.x); minY = Math.min(minY, it.y);
      maxX = Math.max(maxX, it.x + it.w); maxY = Math.max(maxY, it.y + it.h);
    });
    items.forEach(function (it) { it.x -= minX; it.y -= minY; });
    return { items: items, w: maxX - minX, h: maxY - minY };
  }

  /* ---------- drawing ---------- */
  /* real wood grain: the same 9-slice frame pictures as the product page (assets/mikoo-frame-<tex>.jpg, 48 px band),
     loaded on first use; until one has loaded the flat colours are drawn */
  var TEX_BAND = 48;
  var textures = {};
  function texture(name) {
    if (!name || !dialog) return null;
    var t = textures[name];
    if (!t) {
      var url = dialog.getAttribute('data-tex-' + name);
      if (!url) return null;
      t = textures[name] = new Image();
      t.crossOrigin = 'anonymous';
      t.onload = function () { t.mkReady = true; draw(); };
      t.src = url;
    }
    return t.mkReady ? t : null;
  }
  /* like CSS border-image with stretch: four corners as they are, the four sides stretched along their length */
  function drawGrain(c, tex, x, y, fw, fh, b) {
    var N = tex.naturalWidth, B = TEX_BAND, m = N - 2 * B;
    c.drawImage(tex, 0, 0, B, B, x, y, b, b);
    c.drawImage(tex, N - B, 0, B, B, x + fw - b, y, b, b);
    c.drawImage(tex, 0, N - B, B, B, x, y + fh - b, b, b);
    c.drawImage(tex, N - B, N - B, B, B, x + fw - b, y + fh - b, b, b);
    c.drawImage(tex, B, 0, m, B, x + b, y, fw - 2 * b, b);
    c.drawImage(tex, B, N - B, m, B, x + b, y + fh - b, fw - 2 * b, b);
    c.drawImage(tex, 0, B, B, m, x, y + b, b, fh - 2 * b);
    c.drawImage(tex, N - B, B, B, m, x + fw - b, y + b, b, fh - 2 * b);
  }

  function drawFrame(c, x, y, it, L, s) {
    var def = L.def;
    var img = it.img;
    var fw = it.w * s;
    var fh = it.h * s;
    var b = L.profile * s;
    var depth = (def ? def.dims.depth : 0.6) * s;

    /* drop shadow on the wall */
    c.save();
    c.shadowColor = 'rgba(0, 0, 0, 0.22)';
    c.shadowBlur = depth * 1.4;
    c.shadowOffsetX = depth * 0.25;
    c.shadowOffsetY = depth * 0.45;
    c.fillStyle = def ? def.base : '#ffffff';
    c.fillRect(x, y, fw, fh);
    c.restore();

    /* mitred frame: top light, right and bottom dark, left base (as in mikoo-frame.js) */
    var grain = def && b > 0 ? texture(def.tex) : null;
    if (grain) {
      drawGrain(c, grain, x, y, fw, fh, b);
    } else if (def && b > 0) {
      var sides = [
        [def.light, [[x, y], [x + fw, y], [x + fw - b, y + b], [x + b, y + b]]],
        [def.dark, [[x + fw, y], [x + fw, y + fh], [x + fw - b, y + fh - b], [x + fw - b, y + b]]],
        [def.dark, [[x, y + fh], [x + fw, y + fh], [x + fw - b, y + fh - b], [x + b, y + fh - b]]],
        [def.base, [[x, y], [x + b, y + b], [x + b, y + fh - b], [x, y + fh]]]
      ];
      sides.forEach(function (side) {
        c.fillStyle = side[0];
        c.beginPath();
        side[1].forEach(function (pt, i) { if (i) c.lineTo(pt[0], pt[1]); else c.moveTo(pt[0], pt[1]); });
        c.closePath();
        c.fill();
      });
    }

    /* the picture, filling the opening like object-fit: cover */
    var ix = x + b, iy = y + b, iw = fw - 2 * b, ih = fh - 2 * b;
    c.fillStyle = '#ffffff';
    c.fillRect(ix, iy, iw, ih);
    if (img) {
      var r = Math.max(iw / img.naturalWidth, ih / img.naturalHeight);
      var sw = iw / r, sh = ih / r;
      c.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh, ix, iy, iw, ih);
    }

    /* the shadow the frame lip throws on the picture */
    if (def) {
      var recess = Math.max(1, depth * 0.16);
      var top = c.createLinearGradient(0, iy, 0, iy + recess);
      top.addColorStop(0, 'rgba(0, 0, 0, 0.18)');
      top.addColorStop(1, 'rgba(0, 0, 0, 0)');
      c.fillStyle = top;
      c.fillRect(ix, iy, iw, recess);
      var left = c.createLinearGradient(ix, 0, ix + recess, 0);
      left.addColorStop(0, 'rgba(0, 0, 0, 0.14)');
      left.addColorStop(1, 'rgba(0, 0, 0, 0)');
      c.fillStyle = left;
      c.fillRect(ix, iy, recess, ih);
    }
    c.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    c.lineWidth = Math.max(0.5, s * 0.08);
    c.strokeRect(ix, iy, iw, ih);
  }

  /* the whole scene in photo pixels; used for the screen and for the saved image */
  function paint(c) {
    c.drawImage(photo, 0, 0);
    if (mode === 'measure') return paintMeasure(c);
    if (mode !== 'place') return;
    var L = layout();
    var s = pxPerCm;
    var left = art.x - (L.w * s) / 2;
    var top = art.y - (L.h * s) / 2;
    L.items.forEach(function (it) {
      drawFrame(c, left + it.x * s, top + it.y * s, it, L, s);
    });
  }

  function paintMeasure(c) {
    var k = view.k;
    var a = line.a, b = line.b;
    c.save();
    c.lineCap = 'round';
    c.strokeStyle = 'rgba(255, 255, 255, 0.95)';
    c.lineWidth = 5 / k;
    c.beginPath(); c.moveTo(a.x, a.y); c.lineTo(b.x, b.y); c.stroke();
    c.strokeStyle = '#121214';
    c.lineWidth = 2 / k;
    c.setLineDash([7 / k, 5 / k]);
    c.beginPath(); c.moveTo(a.x, a.y); c.lineTo(b.x, b.y); c.stroke();
    c.setLineDash([]);
    [a, b].forEach(function (p) {
      c.beginPath();
      c.arc(p.x, p.y, HANDLE_PX / k, 0, Math.PI * 2);
      /* see-through ring, so the edge being marked stays visible under it */
      c.fillStyle = 'rgba(255, 255, 255, 0.18)';
      c.fill();
      c.lineWidth = 4 / k;
      c.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      c.stroke();
      c.lineWidth = 2 / k;
      c.strokeStyle = '#121214';
      c.stroke();
      c.beginPath();
      c.arc(p.x, p.y, 2.2 / k, 0, Math.PI * 2);
      c.fillStyle = '#121214';
      c.fill();
    });
    var cm = parseFloat(dialog.querySelector('[data-mk-wp-cm]').value);
    var label = cm > 0 ? cm + ' ס״מ' : '? ס״מ';
    c.font = '600 ' + 14 / k + 'px Assistant, sans-serif';
    c.textAlign = 'center';
    c.textBaseline = 'bottom';
    var mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2 - 12 / k;
    var tw = c.measureText(label).width + 14 / k;
    c.fillStyle = '#121214';
    c.fillRect(mx - tw / 2, my - 22 / k, tw, 22 / k);
    c.fillStyle = '#ffffff';
    c.fillText(label, mx, my - 4 / k);
    c.restore();
  }

  function draw() {
    if (drawQueued || !photo) return;
    drawQueued = true;
    requestAnimationFrame(function () {
      drawQueued = false;
      ctx.setTransform(view.k * view.dpr, 0, 0, view.k * view.dpr, 0, 0);
      ctx.clearRect(0, 0, photo.width, photo.height);
      paint(ctx);
    });
  }

  /* fit the photo into the dialog */
  function fit() {
    if (!photo) return;
    var box = canvas.parentElement;
    var maxW = box.clientWidth;
    var maxH = Math.max(220, window.innerHeight * (window.innerWidth < 750 ? 0.5 : 0.58));
    view.k = Math.min(maxW / photo.width, maxH / photo.height);
    view.dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.style.width = Math.round(photo.width * view.k) + 'px';
    canvas.style.height = Math.round(photo.height * view.k) + 'px';
    canvas.width = Math.round(photo.width * view.k * view.dpr);
    canvas.height = Math.round(photo.height * view.k * view.dpr);
    draw();
  }

  /* ---------- the wall: a photo or the sample wall ---------- */
  function usePhoto(file) {
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      var r = Math.min(1, MAX_PHOTO / Math.max(img.naturalWidth, img.naturalHeight));
      photo = document.createElement('canvas');
      photo.width = Math.round(img.naturalWidth * r);
      photo.height = Math.round(img.naturalHeight * r);
      photo.getContext('2d').drawImage(img, 0, 0, photo.width, photo.height);
      URL.revokeObjectURL(url);
      isSample = false;
      track('photo');
      startMeasure();
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      showStep('start');
      dialog.querySelector('.mk-wp__note').textContent = 'לא הצלחנו לפתוח את התמונה. נסו תמונה אחרת (JPG או PNG).';
    };
    img.src = url;
  }

  /* a plain wall with the sofa from the wall drawing, at a known scale */
  function useSample() {
    var S = SAMPLE;
    photo = document.createElement('canvas');
    photo.width = S.wallCm * S.pxPerCm;
    photo.height = S.heightCm * S.pxPerCm;
    var c = photo.getContext('2d');
    var floorY = photo.height - S.floorCm * S.pxPerCm;
    var wall = c.createLinearGradient(0, 0, 0, floorY);
    wall.addColorStop(0, '#f6f3ee');
    wall.addColorStop(1, '#ebe5dc');
    c.fillStyle = wall;
    c.fillRect(0, 0, photo.width, floorY);
    c.fillStyle = '#d9cfc0';
    c.fillRect(0, floorY, photo.width, photo.height - floorY);
    c.fillStyle = 'rgba(0, 0, 0, 0.06)';
    c.fillRect(0, floorY - 2, photo.width, 4);
    pxPerCm = S.pxPerCm;
    isSample = true;
    track('sample');

    var sofaW = S.sofaCm * S.pxPerCm, sofaH = S.sofaHeightCm * S.pxPerCm;
    var sofaX = (photo.width - sofaW) / 2, sofaY = floorY - sofaH + 1.5 * S.pxPerCm;
    var svg = document.querySelector('.mk-wall__sofa svg');
    function place() {
      startPlace(photo.width / 2, sofaY - 25 * S.pxPerCm, true); /* 25 cm above the sofa back */
    }
    if (!svg) return place();
    var markup = new XMLSerializer().serializeToString(svg);
    if (markup.indexOf('xmlns=') < 0) markup = markup.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
    var img = new Image();
    img.onload = function () { c.drawImage(img, sofaX, sofaY, sofaW, sofaH); place(); };
    img.onerror = place;
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(markup);
  }

  /* ---------- steps ---------- */
  function showStep(name) {
    dialog.querySelectorAll('[data-step]').forEach(function (el) { el.hidden = el.dataset.step !== name; });
  }

  function showPanel(name) {
    dialog.querySelectorAll('[data-panel]').forEach(function (el) { el.hidden = el.dataset.panel !== name; });
  }

  function startMeasure() {
    mode = 'measure';
    showStep('view');
    showPanel('measure');
    hint.textContent = 'גררו את שתי הנקודות לקצוות של משהו שאתם יודעים את האורך שלו (דף A4 שהדבקתם, דלת, ספה), ורשמו את האורך.';
    var w = photo.width, h = photo.height;
    line.a = { x: w * 0.35, y: h * 0.62 };
    line.b = { x: w * 0.65, y: h * 0.62 };
    fit();
    updateMeasured();
  }

  function updateMeasured() {
    var cm = parseFloat(dialog.querySelector('[data-mk-wp-cm]').value);
    dialog.querySelector('[data-mk-wp-measured]').disabled = !(cm >= 5);
    dialog.querySelectorAll('.mk-wp__chip[data-cm]').forEach(function (chip) {
      chip.setAttribute('aria-pressed', parseFloat(chip.dataset.cm) === cm ? 'true' : 'false');
    });
    draw();
  }

  function finishMeasure() {
    var cm = parseFloat(dialog.querySelector('[data-mk-wp-cm]').value);
    var px = Math.hypot(line.b.x - line.a.x, line.b.y - line.a.y);
    if (!(cm >= 5) || px < 10) return;
    pxPerCm = px / cm;
    track('measured');
    /* hang the art centred, a little above the middle of the photo */
    startPlace(photo.width / 2, photo.height * 0.42);
  }

  /* bottom: true when y is where the bottom edge hangs (above the sample sofa), not the centre */
  function startPlace(x, y, bottom) {
    mode = 'place';
    art.x = x;
    art.y = bottom ? y - (layout().h * pxPerCm) / 2 : y;
    showStep('view');
    showPanel('place');
    dialog.querySelector('[data-mk-wp-remeasure]').hidden = isSample;
    dialog.querySelector('[data-mk-wp-save]').hidden = !canExport;
    var choices = [sizeIndex, frameIndex].filter(function (i) { return i > -1 && product.options[i].values.length > 1; }).length;
    var what = listed || pictures.length > 1 ? 'הסט' : 'התמונה';
    hint.textContent = (isSample ? 'קיר לדוגמה עם ספה ברוחב 2 מטר. גררו כדי להזיז את ' + what : 'גררו את ' + what + ' למקום שתרצו') +
      (choices ? ', ובחרו ' + (sizeIndex > -1 ? 'גודל ומסגרת.' : 'מסגרת.') : '.') +
      (listed ? ' כל תמונה בסט מוצגת בגודל שלה.' : '');
    renderOptions();
    fit();
    clampArt(false);
  }

  /* ---------- size and frame buttons ---------- */
  function renderOptions() {
    var box = dialog.querySelector('[data-mk-wp-options]');
    box.textContent = '';
    product.options.forEach(function (option, index) {
      var name = (option.name || option).toString();
      var values = option.values || [];
      if (index !== sizeIndex && index !== frameIndex) return;
      if (values.length < 2) return;
      var group = document.createElement('div');
      group.className = 'mk-wp__group';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', name);
      var label = document.createElement('span');
      label.className = 'mk-wp__group-label';
      label.textContent = name;
      group.appendChild(label);
      values.forEach(function (value) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'mk-wp__chip';
        var exists = product.variants.some(function (v) { return v.options[index] === value; });
        if (!exists) return;
        chip.textContent = value.indexOf(noFrame()) > -1 ? 'ללא מסגרת' : value;
        var colour = frames()[value];
        if (colour) {
          chip.classList.add('mk-wp__chip--frame');
          chip.style.setProperty('--mk-sw', colour.base);
        }
        chip.setAttribute('aria-pressed', variant.options[index] === value ? 'true' : 'false');
        chip.addEventListener('click', function () { choose(index, value); });
        group.appendChild(chip);
      });
      box.appendChild(group);
    });
    var add = dialog.querySelector('[data-mk-wp-add]');
    add.disabled = !variant.available;
    add.textContent = variant.available ? 'הוספה לסל' : 'אזל מהמלאי';
    var L = layout();
    dialog.querySelector('[data-mk-wp-price]').innerHTML =
      '<strong>' + formatMoney(variant.price) + '</strong> · <span dir="ltr">' + Math.round(L.w) + '×' + Math.round(L.h) + '</span> ס״מ על הקיר';
  }

  /* ---------- dragging ---------- */
  function toPhoto(event) {
    var rect = canvas.getBoundingClientRect();
    return { x: (event.clientX - rect.left) / view.k, y: (event.clientY - rect.top) / view.k };
  }

  function artRect() {
    var L = layout();
    var w = L.w * pxPerCm, h = L.h * pxPerCm;
    return { left: art.x - w / 2, top: art.y - h / 2, w: w, h: h };
  }

  /* keep the whole art on the photo (or centred on it, when the art is bigger than the photo) */
  function bounds() {
    var r = artRect();
    var x1 = r.w / 2, x2 = photo.width - r.w / 2;
    var y1 = r.h / 2, y2 = photo.height - r.h / 2;
    return { minX: Math.min(x1, x2), maxX: Math.max(x1, x2), minY: Math.min(y1, y2), maxY: Math.max(y1, y2) };
  }

  function clampArt(animate) {
    var b = bounds();
    var tx = Math.min(b.maxX, Math.max(b.minX, art.x));
    var ty = Math.min(b.maxY, Math.max(b.minY, art.y));
    if (animate && springX) { springX.to(tx); springY.to(ty); }
    else { art.x = tx; art.y = ty; draw(); }
  }

  function onDown(event) {
    if (mode === 'start' || !photo) return;
    event.preventDefault(); /* no text selection when a drag starts beside the art */
    var p = toPhoto(event);
    var hit = (HANDLE_PX + 12) / view.k;
    if (mode === 'measure') {
      var target = Math.hypot(p.x - line.a.x, p.y - line.a.y) < hit ? line.a
        : Math.hypot(p.x - line.b.x, p.y - line.b.y) < hit ? line.b : null;
      if (!target) return;
      drag = { point: target, dx: target.x - p.x, dy: target.y - p.y };
    } else {
      var r = artRect();
      if (p.x < r.left || p.x > r.left + r.w || p.y < r.top || p.y > r.top + r.h) return;
      if (springX) { springX.stop(); springY.stop(); }
      drag = { art: true, dx: art.x - p.x, dy: art.y - p.y, samples: [{ x: art.x, y: art.y, t: event.timeStamp }] };
      canvas.classList.add('is-grabbing');
    }
    canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  function onMove(event) {
    if (!photo) return;
    var p = toPhoto(event);
    if (!drag) {
      if (mode === 'place') {
        var r = artRect();
        canvas.classList.toggle('is-over', p.x >= r.left && p.x <= r.left + r.w && p.y >= r.top && p.y <= r.top + r.h);
      }
      return;
    }
    if (drag.point) {
      drag.point.x = Math.min(photo.width, Math.max(0, p.x + drag.dx));
      drag.point.y = Math.min(photo.height, Math.max(0, p.y + drag.dy));
    } else {
      /* 1:1 with the finger, with soft resistance past the edges */
      var b = bounds();
      var x = p.x + drag.dx, y = p.y + drag.dy;
      art.x = x < b.minX ? b.minX + MikooSpring.rubberband(x - b.minX, photo.width) : x > b.maxX ? b.maxX + MikooSpring.rubberband(x - b.maxX, photo.width) : x;
      art.y = y < b.minY ? b.minY + MikooSpring.rubberband(y - b.minY, photo.height) : y > b.maxY ? b.maxY + MikooSpring.rubberband(y - b.maxY, photo.height) : y;
      drag.samples.push({ x: art.x, y: art.y, t: event.timeStamp });
      if (drag.samples.length > 5) drag.samples.shift();
    }
    draw();
  }

  function onUp() {
    if (!drag) return;
    var d = drag;
    drag = null;
    canvas.classList.remove('is-grabbing');
    if (!d.art) return;
    /* placing is precise work: the art stays where the finger lets go (no flick momentum).
       Only when pulled past an edge does it spring back, carrying the finger's velocity. */
    var b = bounds();
    var tx = Math.min(b.maxX, Math.max(b.minX, art.x));
    var ty = Math.min(b.maxY, Math.max(b.minY, art.y));
    if (tx === art.x && ty === art.y) return;
    if (!window.MikooSpring || reduceMotion) { clampArt(false); return; }
    var first = d.samples[0], last = d.samples[d.samples.length - 1];
    var dt = Math.max(16, last.t - first.t) / 1000;
    springX.set(art.x); springY.set(art.y);
    springX.to(tx, (last.x - first.x) / dt);
    springY.to(ty, (last.y - first.y) / dt);
  }

  /* ---------- add to cart (same request as Dawn's product form, so the drawer opens as usual) ---------- */
  function addToCart() {
    var button = dialog.querySelector('[data-mk-wp-add]');
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    var body = new FormData();
    body.append('id', variant.id);
    body.append('quantity', 1);
    if (cart && cart.getSectionsToRender) {
      body.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
      body.append('sections_url', window.location.pathname);
    }
    var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(url, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body: body })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) throw new Error(response.description || response.message);
        track('add_to_cart');
        close();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mikoo-wall-preview', productVariantId: variant.id, cartData: response });
        }
        if (cart && cart.renderContents) cart.renderContents(response);
        else window.location = (window.routes && window.routes.cart_url) || '/cart';
      })
      .catch(function (error) {
        hint.textContent = (error && error.message) || 'משהו השתבש, נסו שוב.';
      })
      .finally(function () {
        button.removeAttribute('aria-busy');
        button.disabled = !variant.available;
      });
  }

  /* ---------- save / share the picture of the wall ---------- */
  function save() {
    var out = document.createElement('canvas');
    out.width = photo.width;
    out.height = photo.height;
    var c = out.getContext('2d');
    paint(c);
    var fontPx = Math.max(14, photo.width * 0.022);
    c.font = '600 ' + fontPx + 'px Assistant, sans-serif';
    c.textAlign = 'left';
    c.textBaseline = 'bottom';
    c.fillStyle = 'rgba(255, 255, 255, 0.85)';
    var mark = 'mikoo.co.il';
    var mw = c.measureText(mark).width;
    c.fillRect(fontPx * 0.6, photo.height - fontPx * 2.1, mw + fontPx, fontPx * 1.5);
    c.fillStyle = '#121214';
    c.fillText(mark, fontPx * 1.1, photo.height - fontPx * 0.8);
    try {
      out.toBlob(function (blob) {
        if (!blob) return;
        track('save');
        var name = 'mikoo-' + dialog.dataset.handle + '.jpg';
        var file = window.File ? new File([blob], name, { type: 'image/jpeg' }) : null;
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file], title: dialog.dataset.title, text: dialog.dataset.title + ' – ' + location.href }).catch(function () {});
          return;
        }
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = name;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
      }, 'image/jpeg', 0.9);
    } catch (e) {
      dialog.querySelector('[data-mk-wp-save]').hidden = true;
    }
  }

  /* ---------- open and close: a sheet from the bottom on phones, a fade on larger screens ---------- */
  function open() {
    track('open');
    dialog.showModal();
    document.documentElement.classList.add('mk-wp-locked');
    requestAnimationFrame(function () { dialog.classList.add('is-open'); });
    loadProduct().then(function () {
      pickInitialVariant(); /* follow what is selected on the page now */
      if (mode === 'place') { renderOptions(); fit(); }
    }).catch(function () {
      dialog.querySelector('.mk-wp__note').textContent = 'לא הצלחנו לטעון את המוצר. רעננו את הדף ונסו שוב.';
    });
  }

  function close() {
    if (!dialog.open) return;
    dialog.classList.remove('is-open');
    document.documentElement.classList.remove('mk-wp-locked');
    var done = function () { if (!dialog.classList.contains('is-open')) dialog.close(); };
    if (reduceMotion) return done();
    setTimeout(done, 260);
  }

  function ready(fn) {
    return function () {
      var args = arguments;
      loadProduct().then(function () { fn.apply(null, args); });
    };
  }

  function init() {
    dialog = document.querySelector('[data-mk-wp]');
    var opener = document.querySelector('[data-mk-wp-open]');
    if (!dialog || !opener) return;
    canvas = dialog.querySelector('[data-mk-wp-canvas]');
    ctx = canvas.getContext('2d');
    hint = dialog.querySelector('[data-mk-wp-hint]');
    if (window.MikooSpring) {
      springX = new MikooSpring(0, function (v) { art.x = v; draw(); }, { response: 0.45, damping: 1 });
      springY = new MikooSpring(0, function (v) { art.y = v; draw(); }, { response: 0.45, damping: 1 });
    }

    opener.addEventListener('click', open);
    dialog.querySelector('[data-mk-wp-close]').addEventListener('click', close);
    dialog.addEventListener('cancel', function (event) { event.preventDefault(); close(); });
    dialog.addEventListener('click', function (event) { if (event.target === dialog) close(); });

    var file = dialog.querySelector('[data-mk-wp-file]');
    file.addEventListener('change', function () {
      if (file.files && file.files[0]) ready(function () { usePhoto(file.files[0]); file.value = ''; })();
    });
    dialog.querySelector('[data-mk-wp-sample]').addEventListener('click', ready(useSample));

    var cmInput = dialog.querySelector('[data-mk-wp-cm]');
    cmInput.addEventListener('input', updateMeasured);
    cmInput.addEventListener('keydown', function (event) { if (event.key === 'Enter') finishMeasure(); });
    dialog.querySelectorAll('.mk-wp__refs .mk-wp__chip').forEach(function (chip) {
      chip.addEventListener('click', function () { cmInput.value = chip.dataset.cm; updateMeasured(); });
    });
    dialog.querySelector('[data-mk-wp-measured]').addEventListener('click', finishMeasure);
    dialog.querySelector('[data-mk-wp-add]').addEventListener('click', addToCart);
    dialog.querySelector('[data-mk-wp-save]').addEventListener('click', save);
    dialog.querySelector('[data-mk-wp-remeasure]').addEventListener('click', startMeasure);
    dialog.querySelector('[data-mk-wp-restart]').addEventListener('click', function () {
      mode = 'start';
      photo = null;
      showStep('start');
    });

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    window.addEventListener('resize', function () { if (dialog.open) fit(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
